/**
 * Timeline unificado del evento.
 * Consume /api/erp/eventos/{id}/timeline
 * Muestra en orden descendente: cotización, contrato, pagos, hoja, inventario, personal, cierre, edits.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  PartyPopper, FileText, FileSignature, CreditCard, ClipboardList,
  Package, UserCog, History, CheckCircle2, RefreshCcw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICONOS = {
  "party": PartyPopper, "file-text": FileText, "file-sign": FileSignature,
  "credit-card": CreditCard, "clipboard": ClipboardList, "package": Package,
  "user-cog": UserCog, "history": History, "check-circle": CheckCircle2,
};

const COLORES = {
  evento:      "bg-rose-50 border-rose-200 text-rose-900",
  cotizacion:  "bg-blue-50 border-blue-200 text-blue-900",
  contrato:    "bg-indigo-50 border-indigo-200 text-indigo-900",
  pago:        "bg-emerald-50 border-emerald-200 text-emerald-900",
  hoja:        "bg-amber-50 border-amber-200 text-amber-900",
  inventario:  "bg-purple-50 border-purple-200 text-purple-900",
  personal:    "bg-cyan-50 border-cyan-200 text-cyan-900",
  audit:       "bg-stone-50 border-stone-200 text-stone-700",
  cierre:      "bg-emerald-100 border-emerald-300 text-emerald-900",
};

export default function TimelineEvento({ eventoId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const cargar = useCallback(async () => {
    if (!eventoId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/erp/eventos/${eventoId}/timeline`);
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [eventoId]);

  useEffect(() => { cargar(); }, [cargar]);

  return (
    <Card className="border-stone-200" data-testid="timeline-evento">
      <CardHeader className="flex flex-row items-center justify-between pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <History className="w-4 h-4" /> Historial del evento
          {data && <span className="text-xs text-stone-500">({data.total})</span>}
        </CardTitle>
        <Button variant="outline" size="sm" onClick={cargar} data-testid="btn-refresh-timeline">
          <RefreshCcw className="w-3 h-3" />
        </Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-sm text-stone-500">Cargando historial…</p>
        ) : !data || data.items.length === 0 ? (
          <p className="text-sm text-stone-400 italic">Aún no hay actividad registrada.</p>
        ) : (
          <ol className="relative border-l-2 border-stone-200 pl-4 space-y-3 max-h-[500px] overflow-y-auto pr-2">
            {data.items.map((item, i) => {
              const Icono = ICONOS[item.icono] || History;
              const cls = COLORES[item.categoria] || COLORES.audit;
              const fecha = item.ts ? new Date(item.ts).toLocaleString("es-MX", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
              return (
                <li key={`${item.categoria}-${i}-${item.ts}`} className="relative" data-testid={`timeline-item-${i}`}>
                  <span className={`absolute -left-[26px] top-0.5 w-4 h-4 rounded-full ${cls} border flex items-center justify-center`}>
                    <Icono className="w-2.5 h-2.5" />
                  </span>
                  <div className={`p-2.5 rounded-lg border ${cls}`}>
                    <div className="flex items-baseline justify-between gap-2">
                      <p className="font-medium text-sm">{item.titulo}</p>
                      <span className="text-[10px] text-stone-500 whitespace-nowrap">{fecha}</span>
                    </div>
                    {item.detalle && <p className="text-xs mt-0.5 opacity-80">{item.detalle}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
    </Card>
  );
}
